import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

const getBorderStyle = (border) => {
  if (!border || !border.style) return '';
  const color = border.color?.rgb ? `#${border.color.rgb}` : '#000';
  const thickness = border.style === 'thick' ? '2px' : '1px';
  const styleMap = {
    thin: 'solid',
    medium: 'solid',
    thick: 'solid',
    dotted: 'dotted',
    dashed: 'dashed',
    double: 'double',
  };
  const lineStyle = styleMap[border.style] || 'solid';
  return `${thickness} ${lineStyle} ${color}`;
};

const getCellStyle = (cell) => {
  const style = {
    padding: '4px 8px',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    userSelect: 'none',
  };

  if (!cell?.s) return style;

  const { fill, font, alignment, border } = cell.s;

  if (fill?.fgColor?.rgb) style.backgroundColor = `#${fill.fgColor.rgb}`;
  if (font?.bold) style.fontWeight = 'bold';
  if (font?.color?.rgb) style.color = `#${font.color.rgb}`;
  if (alignment?.horizontal) style.textAlign = alignment.horizontal;
  if (alignment?.vertical) style.verticalAlign = alignment.vertical;

  if (border) {
    if (border.top) style.borderTop = getBorderStyle(border.top);
    if (border.bottom) style.borderBottom = getBorderStyle(border.bottom);
    if (border.left) style.borderLeft = getBorderStyle(border.left);
    if (border.right) style.borderRight = getBorderStyle(border.right);
  } else {
    style.border = '1px solid #e0e0e0'; // By default to avoid excessive spacing
  }

  return style;
};

const columnWidthToPx = (wch = 8) => Math.floor(wch * 7.2);
const rowHeightToPx = (hpt = 15) => Math.floor(hpt * 1.33);

const ROWS_PER_PAGE = 1000;

const ExcelViewer = ({ url, onLoad = () => null }) => {
  const [sheets, setSheets] = useState([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const activeSheet = sheets[activeSheetIndex];

  useEffect(() => {
    const fetchExcel = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer.byteLength === 0) {
          throw new Error('File empty');
        }
        const workbook = XLSX.read(arrayBuffer, {
          type: 'array',
          cellStyles: true,
        });

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          throw new Error('The file does not contain valid sheets');
        }

        const sheetsData = workbook.SheetNames.map((name) => {
          const sheet = workbook.Sheets[name];
          const ref = sheet['!ref'];

          if (!ref) {
            return {
              name,
              data: [],
              colWidths: [],
              rowHeights: [],
            };
          }

          const range = XLSX.utils.decode_range(ref);
          const merges = sheet['!merges'] || [];
          const colWidths = sheet['!cols'] || [];
          const rowHeights = sheet['!rows'] || [];

          //Map the data with styles
          const rows = [];
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const row = [];
            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
              const cell = sheet[cellAddress];

              //Check if the cell is covered by a merge
              const merge = merges.find(
                (m) => R >= m.s.r && R <= m.e.r && C >= m.s.c && C <= m.e.c
              );

              if (merge) {
                if (merge.s.r === R && merge.s.c === C) {
                  row.push({
                    value: cell?.v,
                    colSpan: merge.e.c - merge.s.c + 1,
                    rowSpan: merge.e.r - merge.s.r + 1,
                    style: getCellStyle(cell),
                  });
                } else {
                  row.push(null); // cell covered by another
                }
              } else {
                row.push({
                  value: cell?.v,
                  style: getCellStyle(cell),
                });
              }
            }
            rows.push(row);
          }

          return {
            name,
            data: rows,
            colWidths: colWidths.map((col) => columnWidthToPx(col?.wch || 8)),
            rowHeights: rowHeights.map((row) => rowHeightToPx(row?.hpt || 15)),
          };
        });

        setSheets(sheetsData);
        onLoad();
      } catch (err) {
        setError(`File could not be opened`);
        console.error('Error loading Excel file:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExcel();
  }, [url]);

  useEffect(() => {
    setCurrentPage(0); // Reset page when changing sheet
  }, [activeSheetIndex]);

  useEffect(() => {
    if (sheets.length > 0) onLoad();
  }, [sheets.length]);

  if (isLoading) return <div>Load file...</div>;

  const totalRows = activeSheet.data.length;
  const totalPages = Math.ceil(totalRows / ROWS_PER_PAGE);
  const visibleRows = activeSheet.data.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE
  );
  if (error)
    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#fff0f0',
          border: '1px solid #ffcccc',
          color: '#ff0000',
          borderRadius: '4px',
          textAlign: 'center',
          margin: '20px',
        }}
      >
        {error}
      </div>
    );

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: '75vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Table with content */}
      <div style={{ overflow: 'auto', flex: 1 }}>
        <div style={{ minWidth: 'max-content' }}>
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {visibleRows.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ height: activeSheet.rowHeights?.[rowIndex] || 'auto' }}>
                  {row.map((cell, colIndex) =>
                    cell ? (
                      <td
                        key={colIndex}
                        colSpan={cell.colSpan || 1}
                        rowSpan={cell.rowSpan || 1}
                        style={{
                          minWidth: activeSheet.colWidths?.[colIndex] || 80,
                          maxWidth: activeSheet.colWidths?.[colIndex] || 800,
                          height: activeSheet.rowHeights?.[rowIndex] || 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          verticalAlign: cell.style?.verticalAlign || 'top',
                          textAlign: cell.style?.textAlign || 'left',
                          ...cell.style,
                        }}
                      >
                        {cell.value ?? ''}
                      </td>
                    ) : null
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: 8 }}>
          <button disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>
            ⏮ First
          </button>
          <button disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
            ◀ Prev
          </button>
          <span>
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next ▶
          </button>
          <button
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(totalPages - 1)}
          >
            Last ⏭
          </button>
        </div>
      )}
      {/* Sheet tabs  */}
      <div style={{ display: 'flex', backgroundColor: '#EEEEEE' }}>
        {sheets.map((sheet, i) => (
          <button
            key={sheet.name}
            onClick={() => setActiveSheetIndex(i)}
            style={{
              backgroundColor: i === activeSheetIndex ? 'white' : 'transparent',
              color: i === activeSheetIndex ? 'black' : '#0000CA',
              padding: '8px 16px',
              borderRight: i === activeSheetIndex ? '1px solid #B3B3B3' : '',
              borderLeft: i === activeSheetIndex ? '1px solid #B3B3B3' : '',
              borderBottom: i === activeSheetIndex ? '1px solid #B3B3B3' : '',
              cursor: 'pointer',
              borderRadius: 0,
              marginRight: 6,
              textDecoration: i === activeSheetIndex ? 'none' : 'underline',
              fontWeight: i === activeSheetIndex ? 'bold' : 'normal',
            }}
          >
            {sheet.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ExcelViewer;
